import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActiveFiltersByName,
  FilterCatalogByName,
  FilterCatalogItem,
  FilterValueType,
  ScopeOptions,
} from "src/Modules/Arbitrage/types";
import {
  BarChart,
  MonetizationOn,
  RemoveFromQueue,
  MarkChatRead,
  Handshake,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { isEmpty } from "lodash";
import useLocalStorageState from "use-local-storage-state";

interface Props {
  children: React.ReactNode;
}
interface ContextValue {
  filterCatalogByName: FilterCatalogByName;
  isSyncingRemoteFilters: boolean;
  scopedFilterList: FilterCatalogItem[];
  currentScope: ScopeOptions;
  currentEditFilter: FilterCatalogItem | null;
  setCurrentEditFilter: React.Dispatch<
    React.SetStateAction<FilterCatalogItem | null>
  >;
  activeFiltersByName: ActiveFiltersByName;
  setActiveFiltersByName: any;
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const filterCatalogByName: FilterCatalogByName = {
  minVolume: {
    name: "minVolume",
    type: FilterValueType.number,
    displayName: "Min volume",
    icon: BarChart,
    scopes: ["tickers"],
  },
  allQuoteCoins: {
    name: "allQuoteCoins",
    type: FilterValueType.boolean,
    displayName: "All quote coins",
    icon: MonetizationOn,
    scopes: ["profits", "tickers"],
    remoteSyncScopes: ["profits"],
  },
  excludedExchanges: {
    name: "excludedExchanges",
    type: FilterValueType.array,
    displayName: "Excluded exchanges",
    icon: RemoveFromQueue,
    scopes: ["profits", "tickers"],
    remoteSyncScopes: ["profits"],
  },
  onlyAskFutures: {
    name: "onlyAskFutures",
    type: FilterValueType.boolean,
    displayName: "Only ask futures",
    icon: Handshake,
    scopes: ["profits", "tickers"],
    remoteSyncScopes: ["profits"],
  },
  onlyOperated: {
    name: "onlyOperated",
    type: FilterValueType.boolean,
    displayName: "Only traded coins",
    icon: MarkChatRead,
    scopes: ["profits", "tickers"],
    remoteSyncScopes: ["profits"],
  },
};

const ArbitrageFilterContext = createContext<ContextValue | undefined>(
  undefined
);

export const useArbitrageFilters = () => {
  const context = React.useContext(ArbitrageFilterContext);

  if (context === undefined) {
    throw new Error(
      "useArbitrageFilters must be used within a ArbitrageFiltersProvider"
    );
  }
  return context;
};

const scopes = ["tickers", "profits"];

export const ArbitrageFiltersProvider = ({ children }: Props) => {
  const { pathname } = useLocation();

  // Remote sync disabled - always false
  const isSyncingRemoteFilters = false;

  const currentScope = useMemo(
    () =>
      (scopes.includes(pathname.split("/")[2])
        ? pathname.split("/")[2]
        : "") as ScopeOptions,
    [pathname]
  );

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const scopedFilterList = useMemo(
    () =>
      Object.values(filterCatalogByName).filter((f) =>
        f.scopes.includes(currentScope)
      ),
    [currentScope]
  );

  const [_activeFiltersByName, _setActiveFiltersByName] = useLocalStorageState(
    `V3.ArbitrageFilters:${currentScope}`,
    {
      defaultValue: {} as ActiveFiltersByName,
    }
  );

  // All filters now work locally only (remote sync disabled)
  const setActiveFiltersByName = (
    args: any,
    _filterName: string,
    _filterValue: any
  ) => {
    _setActiveFiltersByName(args);
  };

  const setInitialActiveFilters = useCallback(() => {
    const initial = scopedFilterList.reduce((acc, filter) => {
      return filter?.mandatory
        ? {
            ...acc,
            [filter.name]: filter?.defaultValue || null,
          }
        : acc;
    }, {} as ActiveFiltersByName);

    _setActiveFiltersByName(initial);
  }, [scopedFilterList, _setActiveFiltersByName]);

  useEffect(() => {
    isEmpty(_activeFiltersByName) && setInitialActiveFilters();
  }, [_activeFiltersByName, setInitialActiveFilters]);

  const [currentEditFilter, setCurrentEditFilter] =
    useState<FilterCatalogItem | null>(null);

  useEffect(() => {
    setCurrentEditFilter(null);
  }, [isMenuOpen]);

  // Use local filters only (remote sync disabled)
  const consolidatedFilters = useMemo(
    () => ({ ..._activeFiltersByName }),
    [_activeFiltersByName]
  );

  const value = {
    filterCatalogByName,
    isSyncingRemoteFilters,

    scopedFilterList,
    currentScope,

    currentEditFilter,
    setCurrentEditFilter,

    activeFiltersByName: consolidatedFilters,
    setActiveFiltersByName,

    isMenuOpen,
    setIsMenuOpen,
  };

  return (
    <ArbitrageFilterContext.Provider value={value}>
      {children}
    </ArbitrageFilterContext.Provider>
  );
};
